import { useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { CheckBoxLabel } from "../form/components/CheckboxLabel";
import { AgeInfo } from "../form/components/AgeInfo";

import { AuthHeader } from "../form/components/Headers";
import ModalLoadingFull from "../utils/ModalLoadingFull";
import { postData } from "../utils/APIUtilities";
import { postNotification } from "../utils/Notifications";
import { getEnvironmentVariable } from "../global/environmentVariables";
import { redirectUrlAuth, urlParams } from "../utils/urlParams";

export default function OICLinkAccount({
  oic_link_username,
  tokens,
  identityToken,
}) {
  const { state } = urlParams();

  const providerNameValue =
    identityToken?.["cognito:username"]?.split("_")?.[0];
  const providerName =
    providerNameValue?.charAt(0)?.toUpperCase() + providerNameValue?.slice(1);

  const [processing, setProcessing] = useState(false);
  const onSubmit = async (values) => {
    try {
      setProcessing(true);
      const { username, password, policy } = values;
      if (!username) throw new Error("missing username");
      if (!password) throw new Error("missing password");
      if (!policy) throw new Error("missing policy");

      let headers = {
        Authorization: `Bearer ${tokens.accessToken}`,
        Env: "bcp",
      };

      await postData(
        "/users/linkaccounts",
        {
          body: {
            username: username,
            password: password,
          },
        },
        headers
      );

      localStorage.removeItem("oic_link_username");

      const _redirect_uri = localStorage.getItem("redirect_uri");
      localStorage.removeItem("redirect_uri");

      if (_redirect_uri === "close") {
        window.close();
        return;
      }
      const _response_type = localStorage.getItem("response_type");
      localStorage.removeItem("response_type");
      const responseType =
        _response_type?.toLowerCase() === "tokens" ? "tokens" : "code";

      if (responseType === "code") {
        const customUrl = getEnvironmentVariable("API_URL").replace("/v1", "");

        // New endpoint, should return the auth code
        const apiData = await postData(
          "/oauth/storetoken",
          {
            token: tokens.refreshToken,
            redirect_uri: _redirect_uri,
          },
          null,
          customUrl
        );

        // Regular login redirect
        await redirectUrlAuth({
          cognitoProps: {
            code: apiData?.code || apiData?.authorizationCode,
            state: state || localStorage?.getItem("state"),
          },
          redirect_uri: apiData?.redirect_uri || _redirect_uri,
        });
      } else {
        throw new Error("Invalid response type");
      }
    } catch (err) {
      postNotification("error", err);
      setProcessing(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      {processing && <ModalLoadingFull />}
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <AuthHeader
          text="Link Accounts"
          subTitle={`The email attached to this ${providerName} identity is already assigned to a BCP Account, please enter your BCP username and password to link your account.`}
        />

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Formik
              initialValues={{
                username: oic_link_username || identityToken?.email || null,
                password: "",
                policy: false,
              }}
              enableReinitialize
              validationSchema={Yup.object().shape({
                username: Yup.string().max(128).required("Required"),
                password: Yup.string().max(255).required("Required"),
                policy: Yup.boolean().oneOf([true], "Required"),
              })}
              onSubmit={onSubmit.bind(this)}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue,
                touched,
                values,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.username && errors.username)}
                        helperText={touched.username && errors.username}
                        autoComplete="username"
                        name="username"
                        value={values.username}
                        label="Username"
                        variant="outlined"
                        required
                        fullWidth
                        onBlur={handleBlur}
                        onChange={(e) => {
                          const trimmedString = e.target.value
                            .trim()
                            .toLowerCase();
                          setFieldValue("username", trimmedString);
                        }}
                        disabled={Boolean(oic_link_username)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                        autoComplete="password"
                        name="password"
                        value={values.password}
                        type="password"
                        label="Password"
                        variant="outlined"
                        required
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Grid>

                    <AgeInfo />

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="policy"
                            checked={values.policy}
                            onChange={setFieldValue.bind(
                              this,
                              "policy",
                              !values.policy
                            )}
                          />
                        }
                        label={
                          <CheckBoxLabel
                            text="I have read and agree with the Terms and Conditions"
                            required
                          />
                        }
                      />

                      {Boolean(touched.policy && errors.policy) && (
                        <FormHelperText error>{errors.policy}</FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Button type="submit" fullWidth variant="contained">
                        Link
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
